import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { RenderTarget, WebLink } from '@news-mono/web-common'
import {
    colors,
    easing,
    fonts,
    metrics,
    calcRem,
    breakpoint,
} from '../../../__styling'

import { StyledArticleVideoContainer } from '../../../publication/ArticleVideo/ArticleVideo.styled'
import { tokens } from '@news-mono/design-tokens'
const shareIcon = require('./assets/share.png')
interface StyledHeroContainerProps {
    renderTarget?: RenderTarget
    isGallery?: boolean
}

export const StyledBreadcrumbDesktopWrapper = styled('div')({
    display: 'block',
    marginBottom: calcRem(16),
})

export const StyledHeroContainer = styled('div')<StyledHeroContainerProps>(
    ({ renderTarget, isGallery }) => ({
        display: 'grid',
        gridTemplateRows: 'auto auto',
        gridRowGap: isGallery ? calcRem(0) : calcRem(24),
        gridTemplateColumns: '1fr',
        alignItems: 'self-start',
        marginBottom: calcRem(metrics.sevennews.margins.lg),
        textDecoration: 'none',
        color: 'inherit',
        [breakpoint('sm')]: {
            gridRowGap: calcRem(24),
        },
        [breakpoint('md')]: {
            gridTemplateColumns: '40% calc(60% - 1rem)',
            gridTemplateRows: 'auto',
            columnGap: calcRem(16),
        },
        '& [class*="StyledFigcaption"]': {
            marginTop: 0,
        },

        paddingBottom: renderTarget === 'amp' ? calcRem(20) : 'inherit',
        [`${StyledArticleVideoContainer}`]: {
            marginBottom: 0,
        },
    }),
)

export const StyledTextWrap = styled('div')(({}) => ({
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
    color: 'inherit',
    gridRow: 1,
    [breakpoint('md')]: {
        marginRight: calcRem(30),
        gridRow: 1,
        height: '100%',
        justifyContent: 'space-between',
    },
}))

export const StyledTextWrapHeader = styled('div')(() => ({
    marginBottom: calcRem(12),
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${colors.black20}`,
    height: '100%',

    [breakpoint('sm')]: {
        height: calcRem(34),
    },
    [breakpoint('md')]: {
        height: calcRem(39),
    },
}))

export const StyledInformationWrapper = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
})

export const StyledControlsWrapper = styled('div')({
    display: 'flex',
})

interface ShareWrapperProps {
    isOpened: boolean
}

export const StyledShareWrapper = styled('div')<ShareWrapperProps>(
    ({ isOpened }) => ({
        width: calcRem(18),
        height: '100%',
        cursor: 'pointer',
        marginRight: calcRem(18),
        borderBottom: `${calcRem(3)} solid ${colors.sevennews.red}`,
        '&:hover': {
            '& svg path': {
                fill: colors.sevennews.red,
            },
        },
        '& svg path': {
            fill: isOpened ? colors.sevennews.red : undefined,
        },
        'amp-social-share': {
            backgroundColor: 'transparent',
            backgroundImage: `url(${shareIcon})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            '&:focus': {
                outline: `${calcRem(1)} solid ${
                    tokens.sevennews.colors.palette.red
                }}`,
            },
        },
    }),
)

export const StyledPublishedTimeWrapper = styled('div')(({}) => ({
    fontSize: calcRem(12),
    color: colors.sevennews.nero60,
    fontWeight: 600,

    span: {
        color: colors.sevennews.nero30,
    },
}))
interface StyledLastUpdatedTimeWrapperProps {
    isShareOpened?: boolean
}

export const StyledLastUpdatedTimeWrapper = styled(
    'div',
)<StyledLastUpdatedTimeWrapperProps>(({ isShareOpened }) => ({
    fontSize: calcRem(12),
    color: colors.sevennews.nero,
    opacity: isShareOpened ? 0 : '30%',
    fontWeight: 600,
    lineHeight: calcRem(16),
    visibility: isShareOpened ? 'hidden' : 'visible',
    transition: `opacity ${easing.easeOut.med}`,
}))
interface StyledEstimatedTimeProps {
    hasMoreAuthors?: boolean
}
export const StyledEstimatedTime = styled('span')<StyledEstimatedTimeProps>(
    ({ hasMoreAuthors }) => ({
        color: colors.sevennews.nero30,
        fontSize: calcRem(12),
        fontWeight: 600,
        marginLeft: hasMoreAuthors ? calcRem(0) : calcRem(16),
    }),
)
export const StyledTextToSpeachWrapper = styled('div')({
    marginRight: calcRem(15),
    cursor: 'pointer',
})
export const StyledFontSizeWrapper = styled('div')({
    cursor: 'pointer',
})

export const StyledBylineWrap = styled('div')(({}) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center,',
    marginBottom: calcRem(16),
    [breakpoint('md')]: {
        marginBottom: calcRem(8),
    },
}))

export const StyledBylineSectionContainer = styled('div')({
    marginBottom: 0,

    [breakpoint('sm')]: {
        marginBottom: calcRem(16),
        display: 'block',
    },
    [breakpoint('md')]: {
        marginBottom: calcRem(metrics.sevennews.margins.lg),
        marginTop: calcRem(24),
    },
})

export const StyledArticleTitleContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
})

interface StyledTitleProps {
    isGallery?: boolean
}
export const StyledTitle = styled('h1')<StyledTitleProps>(({ isGallery }) => ({
    fontWeight: 400,
    fontFamily: fonts.sevennews.sansSerif,
    color: colors.sevennews.nero,
    fontSize: calcRem(22),
    lineHeight: calcRem(29),
    textAlign: 'left',
    marginTop: isGallery ? 0 : calcRem(4),
    marginBottom: calcRem(4),

    [breakpoint('sm')]: {
        fontSize: calcRem(30),
        lineHeight: '39px',
        marginTop: isGallery ? 0 : calcRem(8),
        marginBottom: calcRem(8),
    },
    [breakpoint('md')]: {
        fontSize: calcRem(36),
        lineHeight: '39px',
        marginTop: isGallery ? calcRem(8) : calcRem(16),
        marginBottom: calcRem(16),
    },
}))

export const StyledLiveStatus = styled('div')(({ theme }) => ({
    background: colors.sevennews.red,
    color: colors.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: calcRem(3, 16, 3, 10),
    lineHeight: calcRem(18),
    marginRight: calcRem(16),
    borderRadius: calcRem(22),
    width: calcRem(87),
    height: calcRem(34),
    marginTop: calcRem(4),
    marginBottom: calcRem(10),
    [breakpoint('xs')]: {
        marginTop: 0,
        marginBottom: 0,
    },
    [`${StyledPulsatingDotLogo}`]: {
        width: calcRem(7),
        height: calcRem(7),
        marginRight: calcRem(9),
        background: colors.white,
        '&::before, &::after': {
            backgroundColor: colors.white,
        },
    },
}))

interface StyledTeaserProps {
    isGallery?: boolean
}

export const StyledTeaser = styled('div')<StyledTeaserProps>(
    ({ isGallery }) => ({
        margin: 0,
        fontWeight: 300,
        fontFamily: fonts.sevennews.sansSerif,
        color: colors.sevennews.nero,
        fontSize: calcRem(20),
        lineHeight: calcRem(27),
        textAlign: 'left',
        marginBottom: calcRem(metrics.sevennews.margins.lg),
        marginTop: isGallery ? calcRem(10) : calcRem(20),

        [breakpoint('sm')]: {
            marginBottom: calcRem(24),
            marginTop: calcRem(20),
        },
        [breakpoint('md')]: {
            fontSize: calcRem(20),
            lineHeight: calcRem(28),
            marginBottom: 'inherit',
            display: 'block',
        },
    }),
)

const PulseRing = keyframes`
0% {
    transform:  scale(.33);
  }
  80%, 100% {
    opacity: 0
  }
`
const PulseDot = keyframes`
    0% {
        transform: scale(.8);
    }
    50% {
        transform: scale(1);
    }
    100% {
        transform: scale(.8);
    }
`

export const StyledPulsatingDotLogo = styled('div')({
    background: '#E6001E',
    height: calcRem(5),
    width: calcRem(5),
    transform: 'translateX(-0%) translateY(-0%)',
    marginBottom: 0,
    marginRight: calcRem(11),
    borderRadius: '50%',
    '&::before': {
        content: '""',
        position: 'relative',
        display: 'block',
        width: '300%',
        height: '300%',
        boxSizing: 'border-box',
        marginLeft: '-100%',
        marginTop: '-100%',
        borderRadius: calcRem(45),
        backgroundColor: '#E6001E',
        animation: `${PulseRing} 1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite`,
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        left: '0',
        top: '0',
        display: 'block',
        width: '100%',
        height: '100%',
        backgroundColor: '#E6001E',
        borderRadius: calcRem(15),
        boxShadow: '0 0 8px rgba(0,0,0,.3)',
        animation: `${PulseDot} 1.25s cubic-bezier(0.455, 0.03, 0.515, 0.955) -.4s infinite`,
    },
})

export const StyledCardMediaWrap = styled('div')(({}) => ({
    gridRow: 2,
    minHeight: calcRem(200),
    [breakpoint('md')]: {
        gridRow: 1,
        alignSelf: 'center',
        display: 'grid',
    },
    '& [class*="StyledMedia"]': {
        marginBottom: 0,
    },
}))

export const StyledSponsoredContent = styled('p')({
    fontSize: calcRem(12),
    fontWeight: 400,
    lineHeight: calcRem(17),
    color: colors.sevennews.bauhausBlack90,
    textAlign: 'left',
    position: 'relative',

    [breakpoint('sm')]: {
        fontSize: calcRem(13),
        lineHeight: 'normal',
    },

    '&::after': {
        content: `''`,
        position: 'absolute',
        top: calcRem(-16),
        left: 0,
        height: calcRem(1),
        width: calcRem(40),
        background: colors.sevennews.nero30,
    },
})

export const StyledSponsoredContentLearnMore = styled(WebLink)({
    textDecoration: 'underline',
    color: colors.sevennews.red,
})

interface StyledShareWrapperProps {
    isOpened: boolean
}
export const StyeldShareWrapper = styled('div')<StyledShareWrapperProps>(
    ({ isOpened }) => ({
        position: 'absolute',
        left: calcRem(18),
        top: 0,
        bottom: 0,
        background: colors.white,
        width: isOpened ? '100%' : 0,
        maxWidth: calcRem(230),
        borderBottom: `3px solid ${colors.sevennews.red}`,
        overflow: 'hidden',
        transition: `width ${easing.easeOut.med}, padding-bottom ${easing.easeOut.med}`,
        paddingBottom: isOpened ? calcRem(20) : 0,
        [breakpoint('xs')]: {
            paddingBottom: 0,
        },
    }),
)

interface StyeldArticleImageWrapperProps {
    isGallery?: boolean
}

export const StyeldArticleImageWrapper = styled(
    'div',
)<StyeldArticleImageWrapperProps>(({ isGallery }) => ({
    position: 'relative',
    display: isGallery ? 'flex' : 'block',
    gap: calcRem(20),
}))

export const StyledBreakingStatus = styled('span')({
    color: colors.sevennews.red,
    lineHeight: calcRem(18),
    marginRight: calcRem(16),
    width: calcRem(87),
    marginTop: calcRem(4),
    marginBottom: calcRem(10),
    [breakpoint('xs')]: {
        marginTop: 0,
        marginBottom: 0,
    },
})

export const StyledGalleryLabel = styled('p')({
    fontWeight: 500,
    fontFamily: fonts.sevennews.sansSerif,
    color: colors.sevennews.trendingRed,
    fontSize: calcRem(24),
    lineHeight: calcRem(34),
    textAlign: 'left',
    margin: 0,
})

interface ScrollButtonProps {
    position: 'left' | 'right'
    isActive?: boolean
}

export const StyledScrollButtonWrapper = styled('div')<ScrollButtonProps>(
    ({ position, isActive }) => ({
        display: 'flex',
        position: 'relative',
        top: 0,
        bottom: 0,
        alignItems: 'center',
        padding: 0,
        cursor: isActive ? 'pointer' : 'not-allowed',
        zIndex: 1,
        img: {
            rotate: position === 'left' ? '180deg' : undefined,
            opacity: isActive ? 1 : 0.2,
        },
    }),
)

interface StyledEstimatedaTimeToReadProps {
    isShareOpened?: boolean
}
export const StyledEstimatedaTimeToRead = styled(
    'span',
)<StyledEstimatedaTimeToReadProps>(({ isShareOpened }) => ({
    color: colors.sevennews.nero50,
    opacity: isShareOpened ? 0 : '30%',
    fontSize: calcRem(12),
    fontWeight: 600,
    lineHeight: calcRem(16),
    visibility: isShareOpened ? 'hidden' : 'visible',
    transition: `opacity ${easing.easeOut.med}`,
}))
export const StyledArticleTimeContainer = styled('span')(() => ({
    display: 'flex',
    alignItems: 'center',
    columnGap: calcRem(8),
    flexWrap: 'wrap',
    paddingBottom: calcRem(12),
    width: '80%',
    [breakpoint('xs')]: {
        width: '100%',
    },
}))
